import React, { useState, useEffect } from 'react';
import { COST_CLASS_LABELS, SCOPE_LABELS } from '../../config/constants';
import { labelFor } from '../../utils/labels';
import api from '../../services/api';
import useSortable from './useSortable';
import HelpIcon from '../HelpIcon';
import { Card, S as UI } from '../../dashboard/ui';
import ShellFilterBar from '../ShellFilterBar';
import { IcoLn } from '../../shell/ActionIcons';
import { emoText } from '../../shell/EmojiIcons';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import EnumSelect from '../EnumSelect';
import { TextInput } from '../inputs/Input';

const COST_CLASS_OPTIONS = ['', 'variable', 'fixed'];
const SCOPE_OPTIONS      = ['', 'band', 'farm', 'location', 'global'];
// 794e — VARIABLE cost cannot be GLOBAL. A variable cost varies with the bands it is driven by, so a
// company-wide variable pool has no driver to distribute it: 793 traced `variable + global` as one of
// the five allocation methods that LAND NOWHERE in E6/E7 — it is silently dropped, not allocated.
// Offering the combination invites a bucket whose cost quietly disappears from every band view.
// OBSERVED at build time: 0 finance_subtypes rows carry default_scope='global' at all, so no existing
// row is affected and nothing needs migrating. Scope-narrowing only; `fixed` keeps the full set.
const scopeOptionsFor = (costClass) =>
  costClass === 'variable' ? SCOPE_OPTIONS.filter(o => o !== 'global') : SCOPE_OPTIONS;

// 798 — REVENUE_CLASS_NOTE. The two-level revenue picker, mirroring the expense Allocate-vs-None
// tree:  UNALLOCATED  |  ALLOCATED -> (Band / Farm / Site / Global).
//
// AXIS 1 (allocated-ness) is carried by default_scope IS NOT NULL — NOT by default_cost_class.
// The 798 ruling asked for default_cost_class='none' on an UNALLOCATED bucket, but that sentinel
// only exists on the LINE table: db/221:9 relaxed finance.cost_class to ('variable','fixed','none'),
// while the BUCKET column is still CHECK (default_cost_class IN ('variable','fixed'))
// (db/070_wp6_2_expense_classification.sql:23-24). Writing 'none' here 400s on
// finance_subtypes_default_cost_class_check (OBSERVED), and relaxing that CHECK is a migration this
// prompt forbids — so default_cost_class stays NULL on both revenue classes and the reader is
// unaffected: db/471:96-106 already resolves "cost_class='none' OR scope IS NULL" to Unallocated,
// so a NULL scope alone reads as Unallocated exactly as the ruling intends.
//   • class = Unallocated  ⇔  default_scope IS NULL   (company-level, no band)
//   • class = Allocated    ⇔  default_scope IN (band, farm, location, global)
//
// AXIS 2 (scope) writes the SAME column the expense page writes (CHECK band/farm/location/global).
// 'Site' is the UI label for scope='location'; no value is added to any CHECK. The scope picker is
// rendered ONLY when class = Allocated.
// This array is the ONLY place the revenue scope set is defined — the component, the payload
// mapping and the table label all read it, so adding/removing a route is a one-line edit here.
// (The expense SCOPE_OPTIONS above is a separate list and is NOT affected by edits to this one.)
export const REVENUE_SCOPE_OPTIONS = [
  { value: 'band',     get label() { return i18n.t('shared.correction.valueField.band_id'); }   },
  { value: 'farm',     get label() { return i18n.t('fpf.farm'); }   },
  { value: 'location', get label() { return i18n.t('alertsPlatform.feedAlert.site'); }   },
  // 'global' is DELIBERATELY absent here (owner ruling on 797 BLOCKER-2: the days-held driver
  // delivers only 54.13% of the global pool to bands, so the first global-scoped revenue leg would
  // break the footing's strict identity). The finance.scope CHECK is UNCHANGED and the EXPENSE
  // picker above still offers Global — this asymmetry is intended, do not "fix" it.
];
export const CLS_ALLOCATED   = 'allocated';
export const CLS_UNALLOCATED = 'unallocated';

// The two sections differ only in these values; the CRUD below is the same generic
// /api/finance-subtypes endpoints for both (server.js textPkCrud passes applies_to through).
const EXPENSE_CFG = {
  appliesTo:  'Expense',
  filterKey:  'finance-subtypes',
  // The expense table keeps EVERY pre-existing row (incl. legacy 'All'/'Financial'/NULL
  // applies_to) — it only excludes the new revenue buckets, so this page is unchanged.
  rowFilter:  r => r.applies_to !== 'Other Revenue',
  heading:    ":money: Expense Categories",
  headingHelp:'field.expense_bucket.applies_to',
  blurb:      'Default cost class and scope pre-fill the expense decision tree. Changing defaults does NOT mutate existing lines.',
  addLabel:   '+ Add Category',
  noun:       'Category',
  namePh:     'Category name',
  emptyText:  'No expense categories defined.',
  showCostClass: true,
  showGroup: true,   // GES-191/892: expense categories can belong to one group from the expense_groups master list
  scopeLabel: 'Default allocation scope',
  scopeHelp:  'field.expense_bucket.default_scope',
};

const REVENUE_CFG = {
  appliesTo:  'Other Revenue',
  filterKey:  'other-revenue-subtypes',
  rowFilter:  r => r.applies_to === 'Other Revenue',
  heading:    ":money: Other Revenue Categories",
  headingHelp: null,
  blurb:      'Non-egg, non-bird revenue buckets (crate scrap, manure, rent…). Allocated buckets carry a default scope that pre-fills the entry form; Unallocated means company-level (no band, no scope). Changing defaults does NOT mutate existing lines.',
  addLabel:   '+ Add Bucket',
  noun:       'Bucket',
  namePh:     'Bucket name',
  emptyText:  'No other-revenue buckets defined.',
  // 778 ruling #4 / 798: default_cost_class is an EXPENSE semantic (variable/fixed). A revenue
  // bucket has no variable/fixed nature, so the field is not offered and is posted as NULL.
  // Allocated-ness is carried by default_scope alone (a scope => allocated, Unallocated => not).
  showCostClass: false,
  scopeLabel: 'Default scope',
  scopeHelp:  null,
};

function SubTypeSection({ cfg, embedded = false }) {
  const { t } = useTranslation('common');
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [message,  setMessage]  = useState('');
  const [groups,   setGroups]   = useState([]);

  // rev_class is UI-only: it is derived from / written back into default_scope (see the note above).
  const blankForm = () => ({
    name: '',
    default_cost_class: '',
    default_scope: '',
    group_id: '',
    rev_class: CLS_UNALLOCATED,
  });
  const [form, setForm] = useState(blankForm);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (cfg.showGroup) api.request('/expense-groups').then(setGroups).catch(() => setGroups([])); }, [cfg.showGroup]);

  async function loadData() {
    try { setData(await api.request('/finance-subtypes').catch(() => [])); }
    finally { setLoading(false); }
  }

  const engineEdit = !!(editing && (data || []).find(r => r.id === editing)?.engine_only);
  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const alloc = form.rev_class === CLS_ALLOCATED;
      const payload = cfg.showCostClass ? {
        name:               form.name,
        applies_to:         cfg.appliesTo,
        default_cost_class: form.default_cost_class || null,
        default_scope:      form.default_scope      || null,
        group_id:           form.group_id || null,
      } : {
        // 798 mapping — see REVENUE_CLASS_NOTE. default_cost_class stays NULL on BOTH revenue
        // classes: the ruled 'none' sentinel is rejected by finance_subtypes_default_cost_class_check
        // (OBSERVED 400), and relaxing that CHECK is a migration this prompt forbids.
        name:               form.name,
        applies_to:         cfg.appliesTo,
        default_cost_class: null,
        default_scope:      alloc ? (form.default_scope || null) : null,
      };
      if (engineEdit) delete payload.name;   // 47225: an engine bucket's label lives in the language catalog (the shown name is the translation, never written back)
      editing
        ? await api.request(`/finance-subtypes/${editing}`, { method: 'PUT',  body: JSON.stringify(payload) })
        : await api.request('/finance-subtypes',             { method: 'POST', body: JSON.stringify(payload) });
      setMessage(editing ? `:check: ${cfg.noun} updated` : `:check: ${cfg.noun} created`);
      setShowForm(false); setEditing(null); setForm(blankForm()); loadData();
    } catch (err) { setMessage('Error: ' + err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete?')) return;
    try { await api.request(`/finance-subtypes/${id}`, { method: 'DELETE' }); loadData(); }
    catch (err) { setMessage('Error: ' + err.message); }
  }

  const { sorted, Th } = useSortable(data.filter(cfg.rowFilter), 'name');

  if (loading) return <div style={{ color: 'var(--muted)' }}>{t('shared.loadingEllipsis')}</div>;
  const actionBtns = !showForm
    ? <button className="btn primary sm" onClick={() => { setShowForm(true); setEditing(null); setForm(blankForm()); setMessage(''); }}>{cfg.addLabel}</button>
    : null;
  return (
    <div>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--ink)' }}>{emoText(cfg.heading)} {cfg.headingHelp && <HelpIcon helpKey={cfg.headingHelp} />}</h2>
          {actionBtns}
        </div>
      )}
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--muted)' }}>
        {emoText(cfg.blurb)}
      </p>

      {message && <div style={{ marginBottom: 10, fontSize: 12, color: message.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>{emoText(message)}</div>}

      {showForm && (
        <Card style={{ marginBottom: 12 }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <div style={lbl}>{t('stgUi.name')}</div>
                <TextInput required style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={cfg.namePh}
                  disabled={engineEdit} title={engineEdit ? t('stgUi.engineBucketRename') : undefined} />
              </div>
              {cfg.showGroup && (
                <div>
                  <div style={lbl}>{t('stgUi.group')}</div>
                  <EnumSelect bare value={form.group_id} onChange={v => setForm({ ...form, group_id: v })} testid="cat-group">
                    <option value="">{t('stgUi.groupNone')}</option>
                    {groups.filter(g => g.is_active || g.id === form.group_id).map(g => <option key={g.id} value={g.id}>{g.name}{g.is_active ? '' : ` ${t('stgUi.groupInactiveTag')}`}</option>)}
                  </EnumSelect>
                </div>
              )}
              {cfg.showCostClass && (
                <div>
                  <div style={lbl}>{t('stgUi.defaultCostClass')} <HelpIcon helpKey="field.expense_bucket.default_cost_class" /></div>
                  {/* Switching to Variable drops a scope of 'global', so no stale value survives behind
                      the narrowed picker — same discipline as the revenue Unallocated switch below. */}
                  <EnumSelect bare value={form.default_cost_class} onChange={v => setForm({
                    ...form,
                    default_cost_class: v,
                    default_scope: v === 'variable' && form.default_scope === 'global' ? '' : form.default_scope,
                  })}>
                    <option value="">{t('stgUi.none2')}</option>
                    {COST_CLASS_OPTIONS.filter(Boolean).map(o => <option key={o} value={o}>{labelFor(COST_CLASS_LABELS, o)}</option>)}
                  </EnumSelect>
                </div>
              )}
              {!cfg.showCostClass && (
                <div>
                  <div style={lbl}>{t('stgUi.class')}</div>
                  {/* Switching to Unallocated drops any scope held in the form, so no stale value
                      can survive behind the hidden picker — the payload writes scope NULL. */}
                  <EnumSelect bare value={form.rev_class} onChange={v => setForm({
                    ...form,
                    rev_class: v,
                    default_scope: v === CLS_ALLOCATED ? form.default_scope : '',
                  })}>
                    <option value={CLS_ALLOCATED}>{t('stgUi.allocated')}</option>
                    <option value={CLS_UNALLOCATED}>{t('enums.attribution.Unallocated')}</option>
                  </EnumSelect>
                </div>
              )}
              {(cfg.showCostClass || form.rev_class === CLS_ALLOCATED) && (
                <div>
                  <div style={lbl}>{cfg.scopeLabel} {cfg.scopeHelp && <HelpIcon helpKey={cfg.scopeHelp} />}</div>
                  <EnumSelect bare required={!cfg.showCostClass} value={form.default_scope} onChange={v => setForm({ ...form, default_scope: v })}>
                    {cfg.showCostClass ? (
                      <>
                        <option value="">{t('stgUi.none2')}</option>
                        {scopeOptionsFor(form.default_cost_class).filter(Boolean).map(o => <option key={o} value={o}>{labelFor(SCOPE_LABELS, o)}</option>)}
                        {/* 794e — a pre-existing variable+global row would otherwise render BLANK, hiding a
                            real stored value (801's lesson). None exist today; this keeps it visible if one
                            ever does, so it can be corrected rather than silently rewritten on the next save. */}
                        {form.default_cost_class === 'variable' && form.default_scope === 'global' && (
                          <option value="global">{labelFor(SCOPE_LABELS, 'global')} {t('stgUi.notAllowedForVariable')}</option>
                        )}
                      </>
                    ) : (
                      <>
                        <option value="">{t('stgUi.selectScope')}</option>
                        {REVENUE_SCOPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{emoText(o.label)}</option>)}
                      </>
                    )}
                  </EnumSelect>
                </div>
              )}
              <button className="btn-primary" type="submit" disabled={saving} style={{ padding: '7px 14px' }}>
                {saving ? t('shared.saving') : editing ? t('stgUi.update') : t('stgUi.create')}
              </button>
              <button className="btn-ghost" type="button" style={{ padding: '7px 14px' }} onClick={() => { setShowForm(false); setEditing(null); }}>{t('shared.cancel')}</button>
            </div>
          </form>
        </Card>
      )}

      {embedded && (
        <ShellFilterBar storageKey={`settings.${cfg.filterKey}.filters`} fixed>
          <div style={{ display: 'flex', width: '100%' }}>
            <span style={{ flex: 1 }} />
            {actionBtns}
          </div>
        </ShellFilterBar>
      )}

      <Card style={{ padding: 0 }}>
        <table style={UI.table}>
          <thead><tr>
            <Th col="name" label={t('alertsPlatform.name')} style={UI.th} />
            {cfg.showGroup && <Th col="parent_group" label={t('stgUi.group')} style={UI.th} />}
            {cfg.showCostClass && <Th col="default_cost_class" label={t('stgUi.defaultCostClass2')} style={UI.th} />}
            {!cfg.showCostClass && <Th col="default_scope" label={t('stgUi.class2')} style={UI.th} />}
            <Th col="default_scope" label={t('stgUi.defaultScope')} style={UI.th} />
            <th style={UI.thr}>{t('alertsPlatform.actions')}</th>
          </tr></thead>
          <tbody>{sorted.map(row => (
            <tr key={row.id}>
              <td style={UI.td}>{row.name}{row.is_system && <span className="chip info" style={{ marginLeft: 6 }} data-testid="system-bucket">{t('stgUi.systemBucket')}</span>}</td>
              {cfg.showGroup && <td style={UI.td}>{row.parent_group || <span style={{ color: 'var(--muted2)' }}>—</span>}</td>}
              {cfg.showCostClass && <td style={UI.td}>{row.default_cost_class ? labelFor(COST_CLASS_LABELS, row.default_cost_class) : <span style={{ color: 'var(--muted2)' }}>—</span>}</td>}
              {!cfg.showCostClass && <td style={UI.td}>{row.default_scope ? t('stgUi.allocated') : t('enums.attribution.Unallocated')}</td>}
              <td style={UI.td}>{emoText(cfg.showCostClass
                ? (row.default_scope ? labelFor(SCOPE_LABELS, row.default_scope) : <span style={{ color: 'var(--muted2)' }}>—</span>)
                : (row.default_scope
                    ? (REVENUE_SCOPE_OPTIONS.find(o => o.value === row.default_scope)?.label ?? row.default_scope)
                    : <span style={{ color: 'var(--muted2)' }}>—</span>))}</td>
              <td style={UI.tdr}>
                <button className="btn-ghost" style={{ padding: '4px 10px', marginRight: 6 }} onClick={() => {
                  setEditing(row.id);
                  setForm({
                    name: row.name || '',
                    default_cost_class: row.default_cost_class || '',
                    default_scope: row.default_scope || '',
                    group_id: row.group_id || '',
                    rev_class: row.default_scope ? CLS_ALLOCATED : CLS_UNALLOCATED,
                  });
                  setShowForm(true); setMessage('');
                }}><IcoLn name="edit" /> {t('shared.correction.actions.edit')}</button>
                {/* db/1018: a system bucket is posted by its id: delete disabled (the API refuses it too), rename allowed */}
                <button className="btn-danger" style={{ padding: '4px 10px' }} disabled={!!row.is_system} title={row.is_system ? t('stgUi.systemBucketNoDelete') : undefined}
                  onClick={() => handleDelete(row.id)}><IcoLn name="trash" /> {t('shared.correction.verbs.delete')}</button>
              </td>
            </tr>
          ))}
          {sorted.length === 0 && <tr><td style={UI.td} colSpan={5}>{cfg.emptyText}</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function FinanceSubTypesSettings({ embedded = false }) {
  return <SubTypeSection cfg={EXPENSE_CFG} embedded={embedded} />;
}

// 798 — the revenue twin. Same table, same CRUD, applies_to='Other Revenue'.
export function OtherRevenueSubTypesSettings({ embedded = false }) {
  return <SubTypeSection cfg={REVENUE_CFG} embedded={embedded} />;
}

const lbl = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 3 };
const inp = { padding: '7px 9px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' };
